import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, switchMap, throwError, timer } from 'rxjs';
import { utils } from '../helper/utils';
import { Loc, Cluster, Locs, Queue, RepairMode, AssigneeList } from '../models/loc.model';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { assignWOFilter, manualApprovalFilter, myPoolFilter, searchWOList } from '../models/manual-approval-filter.model';
import { AssignedDataRes, ManualApprovalHeader } from '../models/manual-approval-header.model';
import { ApprovalMasterData, LocRequest } from '../models/approval-wo.model';
import { Equipment } from '../models/equipment.model';
import { MaintenanceOrderResponse } from '../models/maintenance-order-response.model';
import { TotalCost } from '../models/total-cost-model';
import { ValidationSummary } from '../models/validation-summary.model';
import { ValidationReferenceResponse } from '../models/validation-reference-response-model';
import { MaintenanceOrderChangeResult } from '../models/maintenance-order-change-result.model';
import { MaintenanceOrderPartSerialNumberUpdateRequest } from '../models/maintenance-order-part-serial-number-update-request.model';

@Injectable({
  providedIn: 'root',
})
export class ApprovalWoService {
  filterData: manualApprovalFilter = {};
  constructor(private httpClient: HttpClient) {}

  getEquipmentDetails(workOrderId: string): Observable<Equipment | null> {
    return this.httpClient.get<Equipment>(utils.GetUrlConfig(`maintenance-orders/${workOrderId}/equipment-details`));
  }
  getWorkOrderById(id: string): Observable<MaintenanceOrderResponse> {
    return this.httpClient.get<MaintenanceOrderResponse>(utils.GetUrlConfig(`maintenance-orders/${id}`));
  }
  getTotalCost(workOrderId: string): Observable<TotalCost> {
    return this.httpClient.get<TotalCost>(utils.GetOrderAPIUrl(`maintenance-orders/${workOrderId}/saved-cost`));
  }
  getSummary(workOrderId: string): Observable<ValidationSummary[]> {
    return this.httpClient.get<ValidationSummary[]>(utils.GetUrlConfig(`maintenance-orders/validation-summary/${workOrderId}`));
  }
  getValidationRefData(workOrderId: string): Observable<ValidationReferenceResponse> {
    return this.httpClient.get<ValidationReferenceResponse>(
      utils.GetUrlConfig(`maintenance-orders/validation-referencedata/${workOrderId}`),
    );
  }
  getAuditLogs(workorderId: string): Observable<MaintenanceOrderChangeResult> {
    return this.httpClient.get<MaintenanceOrderChangeResult>(utils.GetOrderAPIUrl(`maintenance-orders/${workorderId}/audit-logs`));
  }
  completeWorkOrder(
    workorderId: string,
    maintainceOrderUpdateAndComplete: MaintenanceOrderPartSerialNumberUpdateRequest | undefined,
  ): Observable<any> {
    return this.httpClient
      .patch<void>(`${utils.GetOrderAPIUrl('maintenance-orders')}/${workorderId}/complete-repair`, maintainceOrderUpdateAndComplete, {
        observe: 'response',
      })
      .pipe(
        switchMap((response) => {
          if (response.status === 200) {
            return this.getWorkOrderById(workorderId);
          } else {
            return throwError(() => response);
          }
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  getApprovalWorkOrders(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: manualApprovalFilter,
  ): Observable<HttpResponse<ManualApprovalHeader[]>> {
    let url: string = `maintenance-orders/approval/all?page=${page}&limit=${pageLimit}`;
    if (sort) {
      url += `&sort=${encodeURIComponent(sort)}`;
    }
    let clonedFilters = structuredClone(filters);
    if (clonedFilters?.fromDate) {
      clonedFilters.fromDate = utils.formatToISODateUTC(clonedFilters.fromDate, true);
    }
    if (clonedFilters?.toDate) {
      clonedFilters.toDate = utils.formatToISODateUTC(clonedFilters.toDate, false);
    }
    return this.httpClient.post<ManualApprovalHeader[]>(utils.GetUrlConfig(url), clonedFilters, { observe: 'response' }).pipe(
      map((response: HttpResponse<ManualApprovalHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
            priorityFlag: item.isPriority === true ? item.isPriority : null,
            id: item.maintenanceOrderNumber,
            totalOrderCostUSD: item.exchangeRate * item.totalOrderCost,
            maintenanceOrderStatusDuration: Date.now() - new Date(item.maintenanceOrderStatusUpdatedTimestamp).getTime(),
            maintenanceOrderStatusName: utils.getMaintenanceOrderStatusText(item.workOrderStatus).description,
          }));

          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
    );
  }

  getRepairModes(): Observable<RepairMode[]> {
    return this.httpClient.get<RepairMode[]>(utils.GetUrlConfig('workorder-assignment-master/repair-modes'));
  }
  getAssigneeList(): Observable<AssigneeList[]> {
    return this.httpClient.get<AssigneeList[]>(utils.GetUrlConfig('identity/user-details'));
  }

  getAllLocList(): Observable<Loc[]> {
    return this.httpClient.get<Loc[]>(utils.GetUrlConfig('ruleconfiguration-master/loc'));
  }
  getAllClusterList(): Observable<Cluster[]> {
    return this.httpClient.get<Cluster[]>(utils.GetUrlConfig('ruleconfiguration-master/clusters'));
  }
  getClusterByRegion(selectedRegion: Locs): Observable<Cluster[]> {
    return this.httpClient.post<Cluster[]>(utils.GetUrlConfig('ruleconfiguration-master/clusterbyloc'), selectedRegion);
  }
  getAllQueueList(): Observable<Queue[]> {
    return this.httpClient.get<Queue[]>(utils.GetUrlConfig('workorder-assignment-master/queues'));
  }

  assignWorkOrders(reqBody: assignWOFilter): Observable<any> {
    return this.httpClient.post(utils.GetUrlConfig('maintenance-orders/assign'), reqBody);
  }
  unassignOnLogout(req: { workOrderId: number | undefined; actionType: string }) {
    return this.httpClient.post<any>(utils.GetUrlConfig('workorder-userassignment/unassign'), req);
  }
  getAssignedWorkOrderList(reqBody: searchWOList, sort?: string): Observable<HttpResponse<ManualApprovalHeader[]>> {
    let url: string = `maintenance-orders/my-work-orders`;
    if (sort) {
      url += `?sort=${encodeURIComponent(sort)}`;
    }
    if (reqBody.searchText) {
      url += `searchText=${reqBody.searchText}`;
    }
    return this.httpClient.post<ManualApprovalHeader[]>(utils.GetUrlConfig(url), '', { observe: 'response' }).pipe(
      map((response: HttpResponse<ManualApprovalHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
            priorityFlag: item.isPriority === true ? item.isPriority : null,
            id: item.maintenanceOrderNumber,
            totalOrderCostUSD: item.exchangeRate * item.totalOrderCost,
            maintenanceOrderStatusDuration: Date.now() - new Date(item.maintenanceOrderStatusUpdatedTimestamp).getTime(),
            maintenanceOrderStatusName: utils.getMaintenanceOrderStatusText(item.workOrderStatus).description,
          }));

          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
    );
  }
  getApprovalMyWorkOrders(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: manualApprovalFilter,
  ): Observable<HttpResponse<ManualApprovalHeader[]>> {
    let url: string = `maintenance-orders/approval/all?page=${page}&limit=${pageLimit}`;
    if (sort) {
      url += `&sort=${encodeURIComponent(sort)}`;
    }
    let clonedFilters = structuredClone(filters);
    if (clonedFilters?.fromDate) {
      clonedFilters.fromDate = utils.formatToISODateUTC(clonedFilters.fromDate, true);
    }
    if (clonedFilters?.toDate) {
      clonedFilters.toDate = utils.formatToISODateUTC(clonedFilters.toDate, false);
    }
    return this.httpClient.post<ManualApprovalHeader[]>(utils.GetUrlConfig(url), clonedFilters, { observe: 'response' }).pipe(
      map((response: HttpResponse<ManualApprovalHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
            priorityFlag: item.isPriority === true ? item.isPriority : null,
            id: item.maintenanceOrderNumber,
            totalOrderCostUSD: item.exchangeRate * item.totalOrderCost,
            maintenanceOrderStatusDuration: Date.now() - new Date(item.maintenanceOrderStatusUpdatedTimestamp).getTime(),
            maintenanceOrderStatusName: utils.getMaintenanceOrderStatusText(item.workOrderStatus).description,
          }));

          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
    );
  }

  getMyQueueWorkOrder(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: myPoolFilter,
  ): Observable<HttpResponse<ManualApprovalHeader[]>> {
    let url: string = `maintenance-orders/my-queue?page=${page}&limit=${pageLimit}&searchText=${filters?.searchText}`;
    if (sort) {
      url += `&sort=${encodeURIComponent(sort)}`;
    }
    let clonedFilters = structuredClone(filters);
    return this.httpClient.post<ManualApprovalHeader[]>(utils.GetUrlConfig(url), '', { observe: 'response' }).pipe(
      map((response: HttpResponse<ManualApprovalHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
            priorityFlag: item.isPriority === true ? item.isPriority : null,
            id: item.maintenanceOrderNumber,
            totalOrderCostUSD: item.exchangeRate * item.totalOrderCost,
            maintenanceOrderStatusDuration: Date.now() - new Date(item.maintenanceOrderStatusUpdatedTimestamp).getTime(),
            maintenanceOrderStatusName: utils.getMaintenanceOrderStatusText(item.workOrderStatus).description,
          }));

          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
    );
  }
  getMyPoolWorkOrder(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: myPoolFilter,
  ): Observable<HttpResponse<ManualApprovalHeader[]>> {
    let url: string = `maintenance-orders/my-pool?page=${page}&limit=${pageLimit}&searchText=${filters?.searchText}`;
    if (sort) {
      url += `&sort=${encodeURIComponent(sort)}`;
    }
    let clonedFilters = structuredClone(filters);
    return this.httpClient.post<ManualApprovalHeader[]>(utils.GetUrlConfig(url), '', { observe: 'response' }).pipe(
      map((response: HttpResponse<ManualApprovalHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
            priorityFlag: item.isPriority === true ? item.isPriority : null,
            id: item.maintenanceOrderNumber,
            totalOrderCostUSD: item.exchangeRate * item.totalOrderCost,
            maintenanceOrderStatusDuration: Date.now() - new Date(item.maintenanceOrderStatusUpdatedTimestamp).getTime(),
            maintenanceOrderStatusName: utils.getMaintenanceOrderStatusText(item.workOrderStatus).description,
          }));

          return new HttpResponse({
            body: mappedBody,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
    );
  }
  setPriorityFlag(req: any): Observable<any[]> {
    return this.httpClient.post<any[]>(utils.GetUrlConfig('work-order-common-pool-flags/update-priority-flag'), req);
  }

  getClusterShopReferenceDetails(input: LocRequest): Observable<ApprovalMasterData[]> {
    const queryString = this.buildQueryString(input);
    let url: string = `workorder-assignment-master/region?${queryString}`;
    return this.httpClient.get<ApprovalMasterData[]>(utils.GetUrlConfig(url), { observe: 'response' }).pipe(
      map((response: HttpResponse<ApprovalMasterData[]>) => {
        const body = response.body || [];
        return body;
      }),
    );
  }
  buildQueryString(request: LocRequest): string {
    let params = new HttpParams();

    Object.keys(request).forEach((key) => {
      const value = request[key as keyof LocRequest];
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return params.toString();
  }

  holdWorkOrder(req: { workOrderId: number | undefined; actionType: string }) {
    return this.httpClient.post<any>(utils.GetUrlConfig('workorder-userassignment/unassign'), req);
  }
  skipWorkOrder(req: { workOrderId: number | undefined; actionType: string }) {
    return this.httpClient.post<any>(utils.GetUrlConfig('workorder-userassignment/unassign'), req);
  }
  getSearchWorkOrderList(req: searchWOList) {
    let url: string = `maintenance-orders/my-work-orders?searchText=${req.searchText}`;
    return this.httpClient.post<ManualApprovalHeader[]>(utils.GetUrlConfig(url), '', { observe: 'response' }).pipe(
      map((response: HttpResponse<ManualApprovalHeader[]>) => {
        const body = response.body;
        if (body) {
          return new HttpResponse({
            body: body,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
    );
  }
  assignWO(req: searchWOList) {
    let url: string = `maintenance-orders/assign/work-order`;
    return this.httpClient.post<AssignedDataRes>(utils.GetUrlConfig(url), req, { observe: 'response' }).pipe(
      map((response: HttpResponse<AssignedDataRes>) => {
        const body = response.body;
        if (body) {
          return new HttpResponse({
            body: body,
            headers: response.headers,
          });
        } else {
          return response;
        }
      }),
    );
  }
  updateWorkOrder(updateRequest: any) {
    if (updateRequest) {
      return this.httpClient.post<any>(utils.GetOrderAPIUrl('maintenance-orders/update-workorder-tpicode'), updateRequest);
    } else {
      return timer(2000);
    }
  }

  private selectedTabVal = new BehaviorSubject<string>('');
  currTabVal = this.selectedTabVal.asObservable();

  sendSelectedTabVal(headers: string) {
    this.selectedTabVal.next(headers);
  }

  onDownload(filters: manualApprovalFilter): Observable<HttpResponse<ManualApprovalHeader[] | null>> {
    let url: string = `maintenance-orders/approval/all/download`;
    let clonedFilters = structuredClone(filters);
    if (clonedFilters?.fromDate) {
      clonedFilters.fromDate = utils.formatToISODateUTC(clonedFilters.fromDate, true);
    }
    if (clonedFilters?.toDate) {
      clonedFilters.toDate = utils.formatToISODateUTC(clonedFilters.toDate, false);
    }

    return this.httpClient.post<ManualApprovalHeader[] | null>(utils.GetUrlConfig(url), clonedFilters, { observe: 'response' }).pipe(
      map((response: HttpResponse<ManualApprovalHeader[] | null>) => {
        return response;
      }),
    );
  }
}
